import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { APP_NAME } from "@/lib/brand";
import { Bike, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";

const IMG = "https://images.unsplash.com/photo-1605271864611-58dd08d10547?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-white lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-slate-900 lg:block">
        <img src={IMG} alt="Officina bici" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-900/30 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-orange-400">Officina · Magazzino · Clienti</p>
          <h2 className="font-heading text-4xl font-extrabold leading-tight">Ogni bici, ogni intervento,<br />tutto in un'unica scheda.</h2>
        </div>
      </div>
      <div className="flex flex-col items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="mb-10 flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-orange-600 text-white"><Bike className="h-6 w-6" /></div>
            <span className="font-heading text-xl font-extrabold text-slate-900">{APP_NAME}</span>
          </div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Accesso staff</h1>
          <p className="mb-8 mt-2 text-sm text-slate-500">Inserisci le tue credenziali per entrare nel gestionale.</p>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
              <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Password</Label>
              <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="login-error">{error}</p>}
            <Button data-testid="login-submit-button" type="submit" disabled={loading} className="h-11 w-full bg-orange-600 text-sm font-semibold hover:bg-orange-700">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
            </Button>
          </div>
        </form>
        <p className="mt-6 text-center text-sm text-slate-500">Hai un negozio di bici? <Link to="/registrati" className="font-semibold text-orange-600 hover:underline" data-testid="login-registrati-link">Prova gratis per {14} giorni</Link></p>
        <Link to="/scopri" className="mt-2 block text-center text-xs text-slate-400 hover:text-slate-600" data-testid="login-scopri-link">Scopri {APP_NAME}: funzioni e prezzi</Link>
      </div>
    </div>
  );
}
